import React, { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import { FaTrash, FaFile } from "react-icons/fa6";
import { useSelector } from "react-redux";

import api from "@/services/api";
import { readFileAsync } from "@/utils";
import Loader from "@/components/loader";
import MultiSelect from "@/components/MultiSelect";

// Define the default Selego prompts
const defaultPrompts = {
  en: `Based on the project description, please generate an initial set of roadmap items with time estimates in days following a Features-First approach based on an MVP.

As a technical partner specializing in Tech, Product, Data/IA & Growth, be assertive in your recommendations and break down the project into:

1. Core functionality items (essential features that MUST be in the MVP)
2. User experience items (necessary for the MVP)
3. Growth & analytics features (required for initial success)

Be decisive in categorizing features - clearly separate what's essential now versus what should come later that you don't include in your response. Focus on delivering maximum value with minimum complexity for the MVP.`,

  fr: `Sur base de la description du projet, génère une série initiale de propositions d'items avec des estimations de temps en jours suivant une approche Features-First basée sur un MVP.

En tant que partenaire technique spécialisé en Tech, Produit, Données/IA & Croissance, soit force de proposition et décomposez le projet en:

1. Items de fonctionnalités essentielles (éléments INDISPENSABLES pour le MVP)
2. Items d'expérience utilisateur (nécessaires pour le MVP)
3. Items de croissance et d'analytique (requis pour le succès initial)

Soit décisif dans la catégorisation des fonctionnalités - sépare clairement ce qui est essentiel maintenant de ce qui devrait venir plus tard que tu n'inclus pas dans ta réponse. Concentre-toi sur la livraison d'une valeur maximale avec une complexité minimale pour le MVP.`,
};

export default ({ project }) => {
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [roadmapFilters, setRoadmapFilters] = useState({
    project_id: project._id,
  });
  const [generatedItems, setGeneratedItems] = useState([]);
  const [isStartingConversation, setIsStartingConversation] = useState(false);
  const [isCreatingItem, setIsCreatingItem] = useState(false);
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const [contextualItems, setContextualItems] = useState([]);
  const [activeTab, setActiveTab] = useState("conversation");
  const [aiConversation, setAiConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.Auth.user);

  useEffect(() => {
    getRoadmapItems();
  }, [roadmapFilters]);

  async function getRoadmapItems() {
    try {
      setLoading(true);
      const { ok, data } = await api.post("/roadlog_item/search", roadmapFilters);
      if (!ok) throw new Error("Failed to fetch roadmap items");
      setRoadmapItems(data);
    } catch (error) {
      console.error("Failed to fetch roadmap items:", error);
      toast.error("Failed to load roadmap items");
    } finally {
      setLoading(false);
    }
  }

  const addItemToRoadmap = async (item) => {
    try {
      setIsCreatingItem(true);
      const { ok, data } = await api.post("/roadlog_item", {
        project_id: project._id,
        title: item.title,
        days_required: item.days_required || item.days || 0,
        resource_profile: "EXPERIMENTED DEVELOPER",
        estimation: Number(item.days_required) * 500 || 0,
        comments: item.comments.map((c) => ({ text: c.text, user_id: user._id, user_name: user.name, user_avatar: user.avatar })),
        module_name: item.module || "",
        module_id: item.module_id || "",
        status: "TODO",
        category: "FEATURE",
      });
      if (!ok) throw new Error("Failed to add item to roadmap");

      setRoadmapItems([...roadmapItems, data]);

      if (generatedItems.some((e) => e.title === item.title)) {
        setGeneratedItems(generatedItems.filter((e) => e.title !== item.title));
      }

      toast.success("Item added to roadmap!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to add item to roadmap");
    } finally {
      setIsCreatingItem(false);
    }
  };

  const addItemToContext = (item) => {
    if (!contextualItems.some((e) => e.title === item.title)) {
      setContextualItems([...contextualItems, item]);
      setActiveTab("conversation");
    }
  };

  const removeItemFromContext = (itemIndex) => {
    setContextualItems(contextualItems.filter((_, index) => index !== itemIndex));
  };

  const deleteItemFromRoadmap = async (itemId) => {
    if (!confirm("Are you sure you want to delete this item from the roadmap?")) return;

    try {
      setIsDeletingItem(true);
      await api.remove(`/roadlog_item/${itemId}`);

      // Update local state
      setRoadmapItems(roadmapItems.filter((item) => item._id !== itemId));

      // Also remove from contextual items if present
      setContextualItems(contextualItems.filter((item) => item._id !== itemId));

      toast.success("Item deleted from roadmap!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete item from roadmap");
    } finally {
      setIsDeletingItem(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="flex flex-1 gap-4 p-4 overflow-hidden">
        <ProjectInfoPanel
          project={project}
          setGeneratedItems={setGeneratedItems}
          setIsStartingConversation={setIsStartingConversation}
          isStartingConversation={isStartingConversation}
          generatedItems={generatedItems}
          isCreatingItem={isCreatingItem}
          addItemToRoadmap={addItemToRoadmap}
          contextualItems={contextualItems}
          removeItemFromContext={removeItemFromContext}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          aiConversation={aiConversation}
          setAiConversation={setAiConversation}
        />

        <div className="w-full md:w-5/12">
          <ExistingItemsPanel
            items={roadmapItems}
            addItemToContext={addItemToContext}
            deleteItemFromRoadmap={deleteItemFromRoadmap}
            isDeletingItem={isDeletingItem}
            loading={loading}
            setRoadmapFilters={setRoadmapFilters}
            roadmapFilters={roadmapFilters}
            project={project}
          />
        </div>
      </div>
    </div>
  );
};

const ProjectInfoPanel = ({
  project,
  setGeneratedItems,
  setIsStartingConversation,
  isStartingConversation,
  generatedItems,
  isCreatingItem,
  addItemToRoadmap,
  contextualItems,
  removeItemFromContext,
  activeTab,
  setActiveTab,
  aiConversation,
  setAiConversation,
}) => {
  const { user } = useSelector((state) => state.Auth);
  const [brief, setBrief] = useState(project.builder_brief);
  const [isSaving, setIsSaving] = useState(false);
  const [showPromptOptions, setShowPromptOptions] = useState(false);

  // Initialize aiResponse (no longer initialized with default prompt)
  const [aiResponse, setAiResponse] = useState("");
  const [builderUrls, setBuilderUrls] = useState(project.builder_urls || []);
  const [uploading, setUploading] = useState(false);
  const conversationEndRef = useRef(null);

  // Reference for dropdown menu
  const promptMenuRef = useRef(null);

  // Handler to close menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (promptMenuRef.current && !promptMenuRef.current.contains(event.target)) {
        setShowPromptOptions(false);
      }
    }

    if (showPromptOptions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPromptOptions]);

  const scrollToBottom = () => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [aiConversation]);

  const sendAiMessage = async () => {
    if (!aiResponse.trim()) return;

    try {
      setIsStartingConversation(true);

      const updatedConversation = [...aiConversation, { role: "user", message: aiResponse }];
      setAiConversation(updatedConversation);

      // Clear textarea immediately after sending message
      setAiResponse("");

      const { data } = await api.post("/project/builder", {
        project_id: project._id,
        conversation: updatedConversation,
        builder_brief: brief,
        context_epics: contextualItems, // Keep the parameter name but pass contextualItems
        epics: generatedItems, // Keep the parameter name but pass generatedItems
        latest_message: aiResponse,
      });

      const finalConversation = [...updatedConversation, { role: "assistant", message: data.message, epics: data.epics || [] }];
      setAiConversation(finalConversation);

      if (data.message) setActiveTab("conversation");

      if (data.epics && Array.isArray(data.epics)) {
        setGeneratedItems(data.epics);
      }

      toast.success("AI response received!");
    } catch (error) {
      console.log(error);
      toast.error("Error connecting to AI");
    } finally {
      setIsStartingConversation(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files);
    if (!uploadedFiles.length) return;

    // Check if files are PDFs
    for (const file of uploadedFiles) {
      if (file.type !== "application/pdf") {
        toast.error("Only PDF files are allowed");
        return;
      }
    }

    setUploading(true);
    try {
      // Prepare files for the multiple upload endpoint
      const filesToUpload = [];

      for (const file of uploadedFiles) {
        // Convert file to base64 for the API
        const filePromise = await readFileAsync(file);
        filesToUpload.push({ rawBody: filePromise, name: file.name });
      }

      // Use the file/multiple endpoint
      const { data: urls } = await api.post("/file/multiple", {
        files: filesToUpload,
        folder: `/project/${project._id}/builder`,
      });

      // Create new document objects
      const newDocuments = urls.map((url, index) => {
        const file = index < uploadedFiles.length ? uploadedFiles[index] : null;
        const fileName = file?.name || `file-${index}`;
        return {
          name: fileName,
          url,
          userId: user._id,
          userName: user.name,
          userAvatar: user.avatar,
          created_at: new Date().toISOString(),
        };
      });

      // Update builderUrls state
      const updatedUrls = [...builderUrls, ...newDocuments];
      setBuilderUrls(updatedUrls);

      // Save the updated URLs to the project
      await api.put(`/project/${project._id}`, {
        builder_brief: brief,
        builder_urls: updatedUrls,
      });

      toast.success("PDF documents uploaded successfully");
    } catch (error) {
      console.error(error);
      toast.error("Error uploading PDF documents");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docIndex) => {
    if (!confirm("Are you sure you want to delete this document?")) return;

    try {
      const updatedUrls = builderUrls.filter((_, index) => index !== docIndex);
      setBuilderUrls(updatedUrls);

      // Save the updated URLs to the project
      await api.put(`/project/${project._id}`, {
        builder_brief: brief,
        builder_urls: updatedUrls,
      });

      toast.success("Document deleted successfully");
    } catch (error) {
      console.error(error);
      toast.error("Error deleting document");
    }
  };

  const saveProjectInfo = async () => {
    try {
      setIsSaving(true);
      await api.put(`/project/${project._id}`, {
        builder_brief: brief,
        builder_urls: builderUrls,
      });
      toast.success("Project information saved successfully!");
    } catch (error) {
      console.log(error);
      toast.error("Error saving project information");
    } finally {
      setIsSaving(false);
    }
  };

  const insertDefaultPrompt = (language) => {
    setAiResponse(defaultPrompts[language]);
    setShowPromptOptions(false);
  };

  return (
    <div className="w-full md:w-8/12 flex flex-col">
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm flex-1 flex flex-col">
        {/* Tab Header */}
        <div className="border-b border-slate-200 bg-slate-50 rounded-t-lg">
          <div className="flex">
            <button
              className={`px-4 py-3 text-sm font-medium rounded-tl-lg transition-colors duration-200 ${
                activeTab === "conversation" ? "text-blue-600 bg-white border-b-2 border-blue-500" : "text-slate-600 hover:text-slate-800 hover:bg-slate-100"
              }`}
              onClick={() => setActiveTab("conversation")}>
              Conversation
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                activeTab === "info" ? "text-blue-600 bg-white border-b-2 border-blue-500" : "text-slate-600 hover:text-slate-800 hover:bg-slate-100"
              }`}
              onClick={() => setActiveTab("info")}>
              Project Details
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {activeTab === "info" && (
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Project Description</label>
                <textarea
                  className="w-full h-[200px] p-3 border border-slate-300 rounded-lg text-slate-700 text-sm resize-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all duration-200"
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  placeholder="Describe your project here..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Project Documents (PDF only)</label>
                <div className="flex items-center justify-center w-full mb-4">
                  <label
                    className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 ${
                      uploading ? "bg-slate-100 border-slate-400" : "border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-400"
                    }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (uploading) return;
                      const droppedFiles = e.dataTransfer.files;
                      if (droppedFiles.length > 0) handlePdfUpload({ target: { files: droppedFiles } });
                    }}>
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {uploading ? (
                        <>
                          <svg className="w-8 h-8 mb-3 text-slate-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <p className="text-sm text-slate-500">Uploading...</p>
                        </>
                      ) : (
                        <>
                          <FaFile className="w-8 h-8 mb-3 text-slate-400" />
                          <p className="text-sm font-medium text-slate-700">
                            <span className="font-semibold text-blue-600">Click to upload PDF</span> or drag and drop
                          </p>
                          <p className="text-xs text-slate-500 mt-1">PDF files only</p>
                        </>
                      )}
                    </div>
                    <input id="pdf-file" type="file" className="hidden" multiple accept="application/pdf" onChange={handlePdfUpload} disabled={uploading} />
                  </label>
                </div>

                {builderUrls && builderUrls.length > 0 && (
                  <div className="space-y-2">
                    {builderUrls.map((doc, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors duration-200">
                        <div className="flex items-center space-x-3 overflow-hidden">
                          <FaFile className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-slate-700 truncate">{doc.name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="text-xs text-emerald-600 hover:text-emerald-800 px-3 py-1.5 rounded-md hover:bg-emerald-50 transition-colors duration-200 font-medium">
                            View
                          </a>
                          <button
                            type="button"
                            onClick={() => handleDeleteDocument(index)}
                            className="text-xs text-red-600 hover:text-red-800 px-2 py-1.5 rounded-md hover:bg-red-50 transition-colors duration-200">
                            <FaTrash className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={saveProjectInfo}
                disabled={isSaving}
                className={`w-full bg-blue-600 text-white px-4 py-3 text-sm font-medium rounded-lg border border-blue-600 hover:bg-blue-700 transition-colors duration-200 ${
                  isSaving ? "opacity-60 cursor-not-allowed" : ""
                }`}>
                {isSaving ? "Saving..." : "Save Information"}
              </button>
            </div>
          )}

          {activeTab === "conversation" && (
            <div className="flex-1 flex flex-col">
              {/* Conversation Area */}
              <div className="flex-1 max-h-[calc(100vh-400px)] overflow-y-auto p-4">
                <div className="space-y-4">
                  {aiConversation.map((message, index) => (
                    <div key={index}>
                      <div className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[85%] p-2 rounded-xl shadow-sm transition-all duration-200 ${
                            message.role === "user" ? "bg-blue-600 text-white rounded-tr-sm" : "bg-slate-100 text-slate-800 rounded-tl-sm border border-slate-200"
                          }`}>
                          <div className="text-sm whitespace-pre-line leading-relaxed">{message.message}</div>
                        </div>
                      </div>

                      {/* Display generated items */}
                      {message.role === "assistant" && index === aiConversation.length - 1 && generatedItems.length > 0 && (
                        <div className="ml-4 mt-4 grid gap-3">
                          {generatedItems.map((item, itemIndex) => {
                            const description = (item.comments || []).length > 0 ? item.comments[0].text : "";
                            return (
                              <div key={itemIndex} className="border border-slate-200 bg-white rounded-lg p-4 shadow-sm transition-all duration-200">
                                <div className="flex justify-between items-start gap-3">
                                  <div className="flex-1">
                                    <div className="flex justify-between items-center mb-3">
                                      <h4 className="text-sm font-semibold text-slate-800 leading-tight" title={item.title}>
                                        {item.title}
                                      </h4>
                                      <button
                                        onClick={() => addItemToRoadmap(item)}
                                        disabled={isCreatingItem}
                                        className={`text-xs text-white px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 transition-colors duration-200 font-medium ${
                                          isCreatingItem ? "opacity-60 cursor-not-allowed" : ""
                                        }`}>
                                        {isCreatingItem ? "Adding..." : "Add to Roadmap"}
                                      </button>
                                    </div>
                                    {description && (
                                      <p className="text-xs text-slate-600 mb-3 leading-relaxed" title={description}>
                                        {description}
                                      </p>
                                    )}
                                    <div className="flex items-center gap-2">
                                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                        {item.days_required} days
                                      </span>
                                      {item.module && (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                                          {item.module}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}

                  {isStartingConversation && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 border border-slate-200 p-4 rounded-xl rounded-tl-sm shadow-sm">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0.4s" }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={conversationEndRef} />
                </div>
              </div>

              {/* Context Items */}
              {contextualItems.length > 0 && (
                <div className="bg-blue-50 border-t border-blue-100 p-3">
                  <div className="flex items-center gap-2">
                    <h4 className="text-xs font-medium text-blue-700">Context:</h4>
                    <div className="flex flex-wrap gap-2">
                      {contextualItems.map((item, index) => (
                        <div key={index} className="inline-flex items-center bg-white border border-blue-200 rounded-full px-3 py-1 text-xs shadow-sm">
                          <span className="font-medium truncate max-w-[160px]" title={item.title}>
                            {item.title}
                          </span>
                          <button
                            onClick={() => removeItemFromContext(index)}
                            className="ml-2 text-slate-400 hover:text-red-500 transition-colors duration-200"
                            title="Remove from context">
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="p-4 border-t border-slate-200 bg-slate-50">
                <div className="flex gap-3 relative" ref={promptMenuRef}>
                  <textarea
                    className="flex-1 p-4 border border-slate-300 rounded-xl text-slate-700 text-xs focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none shadow-sm transition-all duration-200"
                    value={aiResponse}
                    onChange={(e) => setAiResponse(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendAiMessage();
                      }
                    }}
                    placeholder={
                      contextualItems.length > 0 ? "Give your feedback on the selected items or ask for modifications..." : "Ask a question or provide more information..."
                    }
                  />
                  <div className="flex flex-col justify-end gap-2">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowPromptOptions(!showPromptOptions)}
                        className="bg-slate-100 text-slate-700 h-11 px-4 rounded-xl hover:bg-slate-200 transition-colors duration-200 flex items-center justify-center gap-2 border border-slate-300"
                        title="Insert template prompt">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                        </svg>
                        <span className="text-xs font-medium">Template</span>
                      </button>

                      {showPromptOptions && (
                        <div className="absolute bottom-14 right-0 bg-white shadow-lg rounded-lg border border-slate-200 py-2 w-40 z-10">
                          <button
                            onClick={() => insertDefaultPrompt("en")}
                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors duration-200">
                            English
                          </button>
                          <button
                            onClick={() => insertDefaultPrompt("fr")}
                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors duration-200">
                            Français
                          </button>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={sendAiMessage}
                      disabled={isStartingConversation || !aiResponse.trim()}
                      className={`bg-blue-600 text-white px-6 h-11 font-medium rounded-xl hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center ${
                        isStartingConversation || !aiResponse.trim() ? "opacity-60 cursor-not-allowed" : ""
                      }`}>
                      {isStartingConversation ? (
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <line x1="22" y1="2" x2="11" y2="13"></line>
                          <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ExistingItemsPanel = ({ items, addItemToContext, deleteItemFromRoadmap, isDeletingItem, loading, setRoadmapFilters, roadmapFilters, project }) => {
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [groupedItems, setGroupedItems] = useState({});
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [budgets, setBudgets] = useState([]);

  // Group items by module when items change
  useEffect(() => {
    const grouped = items.reduce((acc, item) => {
      const moduleName = item.module_name || "Sans module";
      if (!acc[moduleName]) acc[moduleName] = [];
      acc[moduleName].push(item);
      return acc;
    }, {});
    setGroupedItems(grouped);
    // Open all modules by default
    setExpandedModules(new Set(Object.keys(grouped)));
  }, [items]);

  useEffect(() => {
    fetchBudgets();
  }, [project._id]);

  async function fetchBudgets() {
    try {
      const { data, ok } = await api.post("/budget/search", { projectId: project._id, status: "active" });
      if (!ok) return toast.error("Failed to fetch budgets");
      setBudgets(data);
    } catch (error) {
      console.error("Error fetching budgets:", error);
    }
  }

  const toggleDescription = (itemId) => {
    setExpandedDescriptions({
      ...expandedDescriptions,
      [itemId]: !expandedDescriptions[itemId],
    });
  };

  const toggleModule = (module) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(module)) {
      newExpanded.delete(module);
    } else {
      newExpanded.add(module);
    }
    setExpandedModules(newExpanded);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm h-full flex flex-col">
      <div className="w-full py-3 px-4 border-b border-slate-200 bg-slate-50 rounded-t-lg flex items-center justify-between">
        <div className="flex flex-col items-start gap-2">
          <h3 className="text-sm font-medium text-slate-800">Existing Items</h3>
          <p className="text-xs text-slate-600">{Object.values(groupedItems).flat().length} items in roadmap</p>
        </div>
        <div className="flex flex-col items-start w-2/5 gap-2">
          <div className="w-full">
            <MultiSelect
              id="select-status-roadmap"
              options={["TODO", "READY_TO_DEV", "IN_PROGRESS", "TESTING", "DONE", "ARCHIVED"].map((status) => ({
                value: status,
                label: status,
              }))}
              values={roadmapFilters.statuses?.map((status) => ({ value: status, label: status }))}
              onSelectedChange={(selected) => setRoadmapFilters((f) => ({ ...f, statuses: selected.map((s) => s.value) }))}
              placeholder="Status"
              buttonClassName="w-full px-2.5 py-1.5 text-xs bg-white border-gray-200 rounded-lg transition-colors"
            />
          </div>
          <select
            value={roadmapFilters.budget_id || ""}
            onChange={(e) => {
              if (e.target.value === "no_budget") return setRoadmapFilters((f) => ({ ...f, budget_id: { $exists: false } }));
              setRoadmapFilters((f) => ({ ...f, budget_id: e.target.value || null }));
            }}
            className="w-full px-2.5 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-none">
            <option value="">All budgets</option>
            <option value="no_budget">No budget</option>
            {budgets.map((budget) => (
              <option key={budget._id} value={budget._id}>
                {budget.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="flex-1 overflow-y-auto">
          {Object.keys(groupedItems).length > 0 ? (
            Object.entries(groupedItems).map(([module, moduleItems]) => (
              <div key={module} className="border-b border-slate-100 last:border-b-0">
                <button
                  onClick={() => toggleModule(module)}
                  className="w-full flex items-center justify-between p-2 text-left hover:bg-blue-100 bg-blue-200 transition-colors duration-200">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 text-xs">{module}</span>
                    <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{moduleItems.length}</span>
                  </div>
                  <svg
                    className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${expandedModules.has(module) ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {expandedModules.has(module) && (
                  <div className="bg-white border-b border-slate-100">
                    {moduleItems.map((item, index) => {
                      const description = item.comments && item.comments.length > 0 ? item.comments[0].text : "";
                      const isExpanded = expandedDescriptions[item._id] || false;

                      return (
                        <div key={index} className="border-b border-slate-100 last:border-b-0 px-2 py-1 transition-colors duration-200">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-slate-900 text-xs leading-5 line-clamp-1" title={item.title}>
                                  {item.title}
                                </h4>

                                <button
                                  onClick={() => deleteItemFromRoadmap(item._id)}
                                  disabled={isDeletingItem}
                                  className={`flex-shrink-0 text-red-500 hover:text-red-700 p-[10px] rounded-md hover:bg-red-50 transition-colors duration-200 ${
                                    isDeletingItem ? "opacity-50 cursor-not-allowed" : ""
                                  }`}
                                  title="Delete item">
                                  <FaTrash className="w-3 h-3" />
                                </button>
                              </div>

                              {description && (
                                <p
                                  className={`text-[10px] text-slate-600 leading-relaxed mb-3 ${!isExpanded ? "line-clamp-2" : ""}`}
                                  dangerouslySetInnerHTML={{ __html: description }}></p>
                              )}

                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                    {item.days_required}d
                                  </span>
                                  <span
                                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-medium border ${
                                      item.status === "TODO"
                                        ? "bg-slate-50 text-slate-700 border-slate-100"
                                        : item.status === "IN_PROGRESS"
                                        ? "bg-blue-50 text-blue-700 border-blue-100"
                                        : item.status === "DONE"
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                        : "bg-slate-50 text-slate-700 border-slate-100"
                                    }`}>
                                    {item.status}
                                  </span>
                                </div>

                                <div className="flex items-center gap-2">
                                  {description && description.length > 100 && (
                                    <button
                                      onClick={() => toggleDescription(item._id)}
                                      className="text-[10px] text-blue-600 hover:text-blue-800 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors duration-200">
                                      {isExpanded ? "Less" : "More"}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => addItemToContext(item)}
                                    className="text-[10px] bg-slate-600 text-white px-3 py-1.5 rounded-md hover:bg-slate-700 transition-colors duration-200 font-medium">
                                    Add in context
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center py-12">
                <svg className="w-12 h-12 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <p className="text-slate-500 text-sm">No items in roadmap yet</p>
                <p className="text-slate-400 text-xs mt-1">Start a conversation to generate items</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
